import { useCallback, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import CloseIcon from "@mui/icons-material/Close";
import InputAdornment from "@mui/material/InputAdornment";
import { useQuery } from "react-query";
import {
  useNotify,
  SimpleForm,
  TextInput,
  useResourceContext,
  required,
  maxLength,
  useRefresh,
  useDataProvider,
  useUpdate,
  Edit,
  minLength,
  AutocompleteInput,
  useTranslate,
  useRecordContext,
} from "react-admin";
import { useFormContext } from "react-hook-form";
import MuiDatePickerInput from "../../common/MuiDatePickerInput";
import SaveDeleteBtns from "../../saveDeleteBtns/SaveDeleteBtn";
import DependOnTextInput from "../../common/DependOnTextInput";
import PurchaseItemForm from "./components/PurchaseItemForm";
import currentDate from "../../../utils/currentDate";
import CodeNumberInput from "../../common/CodeNumberInput";

const validateNumber = [required(), minLength(13), maxLength(13)];
const validateDate = [required(), minLength(10), maxLength(10)];
const validateSupplier = [required(), minLength(36), maxLength(36)];
const validateDeliveryNote = [required(), maxLength(14)];
const validateNote = [required()];

const PurchaseOrderIdHydrator = () => {
  const record = useRecordContext();
  const { getValues, setValue } = useFormContext();
  const dataProvider = useDataProvider();

  useEffect(() => {
    let ignore = false;
    const purchaseItems = record?.purchase_items ?? [];
    if (purchaseItems.length === 0) {
      return undefined;
    }

    purchaseItems.forEach((item, index) => {
      const source =
        `purchase_items.${index}.purchase_order_item.purchase_order_id`;
      const purchaseOrderId =
        item?.purchase_order_item?.purchase_order_id ||
        item?.purchase_order_item?.purchase_order?.id;

      if (purchaseOrderId) {
        if (getValues(source) !== purchaseOrderId) {
          setValue(source, purchaseOrderId, {
            shouldDirty: false,
            shouldTouch: false,
            shouldValidate: false,
          });
        }
        return;
      }

      const purchaseOrderItemId =
        item?.purchase_order_item_id || item?.purchase_order_item?.id;
      if (!purchaseOrderItemId) return;

      dataProvider
        .getOne("purchase-order-items", { id: purchaseOrderItemId })
        .then(({ data }) => {
          if (ignore || !data?.purchase_order_id) return;
          if (getValues(source) !== data.purchase_order_id) {
            setValue(source, data.purchase_order_id, {
              shouldDirty: false,
              shouldTouch: false,
              shouldValidate: false,
            });
          }
        })
        .catch(() => {});
    });

    return () => {
      ignore = true;
    };
  }, [record, dataProvider, getValues, setValue]);

  return null;
};

export default function PurchaseEditDialog({ open, setOpen, recordId }) {
  const [update] = useUpdate();
  const notify = useNotify();
  const refresh = useRefresh();
  const resource = useResourceContext(); // cuman nama resource
  const dataProvider = useDataProvider();
  const t = useTranslate();

  // GET SUPPLIER LISTS
  const {
    data: suppliers,
    isLoading: isLoadingSuppliers,
    error: supplierError,
  } = useQuery(["suppliers/select", "getListNoParams"], () =>
    dataProvider.getListNoParams("suppliers/select")
  );
  // GET SUPPLIER LISTS

  const save = useCallback(
    async (values) => {
      const id = values.id ?? recordId;

      try {
        await update(
          resource,
          { id, data: values },
          {
            returnPromise: true,
            onSuccess: (data) => {
              notify("ra.notification.updated", {
                messageArgs: { smart_count: 1 },
              });
              setOpen(false);
              refresh();
            },
          }
        );
      } catch (error) {
        if (error.body && error.body.errors) {
          if (error.body.errors === "403") {
            // munculkan notifikasi erro ketika sudah approved
            notify("mine.notification.403_error", { type: "error" });
          }
          return error.body.errors;
        }
        return undefined;
      }
    },
    [notify, recordId, refresh, resource, setOpen, update]
  );

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      keepMounted={false}
      fullWidth={true}
      maxWidth={false}
    >
      <Edit
        title={" "}
        id={recordId}
        actions={false}
        sx={{ "& .RaEdit-main": { mt: 0 } }}
      >
        <SimpleForm
          warnWhenUnsavedChanges
          onSubmit={save}
          toolbar={<SaveDeleteBtns callback={handleClose} />}
          mode="onBlur"
          reValidateMode="onBlur"
        >
          <PurchaseOrderIdHydrator />
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              pt: 0,
            }}
          >
            <DialogTitle>
              {t("mine.label.edit") + " " + t("mine.expand_menu.purchase")}
            </DialogTitle>
            <IconButton onClick={handleClose} sx={{ mr: "11px" }}>
              <CloseIcon />
            </IconButton>
          </Box>
          <DialogContent sx={{ alignSelf: "normal" }}>
            <TextInput source="id" sx={{ display: "none" }} />

            <CodeNumberInput
              // open={open}
              dependOn="date"
              variant="outlined"
              source="code"
              label="mine.label.receipt_no"
              fullWidth
              validate={validateNumber}
              disabled
            />

            {/* <TextInput
              variant="outlined"
              source="code"
              label="Number"
              // defaultValue={isLoadingNextNumber ? "" : nextNumber}
              fullWidth
              isRequired
              validate={validateNumber}
              disabled
            /> */}
            <MuiDatePickerInput
              variant="outlined"
              source="date"
              fullWidth
              isRequired
              validate={validateDate}
              defaultValue={currentDate()}
              helperText="beda dengan order, ini tidak mempengaruhi harga"
            />
            <AutocompleteInput
              variant="outlined"
              source="supplier_id"
              isRequired
              choices={isLoadingSuppliers ? [] : suppliers}
              fullWidth
              validate={validateSupplier}
            />

            <DependOnTextInput
              dependOn="supplier_id"
              resource="suppliers"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="start">Days</InputAdornment>
                ),
              }}
            />

            <TextInput
              variant="outlined"
              source="delivery_note"
              label="Ref. number"
              fullWidth
              isRequired
              validate={validateDeliveryNote}
            />

            <TextInput
              variant="outlined"
              source="note"
              label="mine.label.note"
              fullWidth
              multiline
              rows={3}
              validate={validateNote}
            />

            <Box
              sx={{
                  maxHeight: '300px', // Sesuaikan tinggi maksimum sesuai kebutuhan Anda
                  overflowY: 'auto',
                  border: '1px solid #ccc', // Opsional: untuk visualisasi batas
                  borderRadius: '4px',
                  p: 1, // Opsional: padding
              }}
              >
              <PurchaseItemForm />
            </Box>
          </DialogContent>
        </SimpleForm>
      </Edit>
    </Dialog>
  );
}
